import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { getPharmacyMedicineHistoryApi } from "../../../services/pharmacyBilling.services";
import { formatRupees } from "../common/money";
import { toId } from "../common/ids";
import HistoryStrip, {
  HistoryNote,
  HistoryLoading,
  rateDelta,
} from "../common/HistoryStrip";

/*
 * Price History, opened under the line it belongs to.
 *
 * The counter's old ERP strip, column for column: the bill it came off, when,
 * how many strips and tablets, which batch, the MRP, the rate, the discount,
 * what that came to per unit, the GST, the expiry and the remark. Only the
 * selected medicine's history is ever shown, so there is never a question of
 * which line a row belongs to - selecting another line moves the panel with
 * it.
 *
 * Folded by default to the last sale alone, every column still there; the
 * rest of the ledger opens on request. A panel that opened on all of them
 * buried the grid it is meant to be read beside.
 */

/* The mockup's own twelve tracks. */
const COLUMNS = [
  { key: "bill", label: "Bill No", width: "80px", tnum: true },
  { key: "date", label: "Date", width: "84px", tnum: false },
  { key: "strip", label: "Strip", width: "44px", align: "ctr" },
  { key: "tab", label: "Tab", width: "36px", align: "ctr" },
  { key: "batch", label: "Batch", width: "minmax(70px, 1fr)" },
  { key: "mrp", label: "MRP", width: "48px", align: "end" },
  { key: "rate", label: "Rate", width: "60px", align: "end" },
  { key: "disc", label: "Disc", width: "40px", align: "end" },
  { key: "net", label: "Net Rate", width: "66px", align: "end" },
  { key: "gst", label: "GST", width: "36px", align: "end" },
  { key: "expiry", label: "Expiry", width: "82px", tnum: false },
  { key: "saleType", label: "Sale Type", width: "minmax(72px, 1.2fr)", tnum: false },
];

/** The first of several names a figure might arrive under. */
const pick = (row, ...names) => {
  for (const name of names) {
    const value = row?.[name];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * What one sale actually charged per unit.
 *
 * The endpoint gives the gross rate and the discount separately, so comparing
 * a rate being typed now against `rate` alone would call a line dearer than it
 * was whenever a discount was given on it. `discount` is the amount off the
 * whole line, so it comes off per unit before the two are compared.
 */
const netRate = (row) => {
  const stated = num(pick(row, "net_rate", "netRate"));
  if (stated !== null) return stated;

  const rate = num(row?.rate) ?? 0;
  const qty = num(row?.quantity) ?? 0;
  const discount = num(row?.discount) ?? 0;
  if (!qty || !discount) return rate;
  return Math.max(rate - discount / qty, 0);
};

/*
 * One row of the strip, from whatever the server called its fields.
 *
 * The endpoint is read rather than specified here, and the columns the old ERP
 * strip has are more than it is known to send - so each is looked for under
 * the names it could plausibly arrive under and prints an em dash where it did
 * not arrive at all. A column the server starts sending fills itself in; one
 * it never sends says so rather than printing a nought that reads as a real
 * figure.
 */
const readRow = (row) => ({
  bill: pick(row, "bill_number", "bill_no", "billNumber"),
  billId: pick(row, "bill_id", "bill", "id"),
  date: row?.date ? dayjs(row.date) : null,
  strip: num(pick(row, "strip", "strip_qty", "strips")),
  tab: num(pick(row, "tab", "tab_qty", "tabs", "loose_qty")),
  quantity: num(row?.quantity),
  batch: pick(row, "batch_number", "batch", "batchNumber"),
  mrp: num(pick(row, "mrp", "batch_mrp", "pharmacy_medicine_mrp")),
  rate: num(row?.rate),
  discount: num(pick(row, "discount_percentage", "discount_percent", "disc")),
  discountAmount: num(row?.discount),
  net: netRate(row),
  gst: num(pick(row, "gst", "gst_percentage", "gst_percent")),
  expiry: pick(row, "expiry", "expiry_date", "batch_expiry"),
  /* How the sale was billed - IP, OP, a counter sale. It used to print under
     a column headed Remark, where it read as a note somebody had written. */
  saleType: pick(row, "sale_type", "type"),
});

/*
 * The discount, as a percentage of what the line would have cost.
 *
 * Shown as a percentage because that is how the counter quotes it, and the
 * endpoint gives it in rupees off the line. Where it sends a percentage of its
 * own that is believed instead.
 */
const discountPercent = (entry) => {
  if (entry.discount !== null) return entry.discount;
  if (!entry.discountAmount || !entry.rate || !entry.quantity) return null;
  const gross = entry.rate * entry.quantity;
  if (!gross) return null;
  return Math.round((entry.discountAmount / gross) * 1000) / 10;
};

const money = (value) => (value === null ? null : formatRupees(value));

const InlinePriceHistory = ({
  patientId,
  patientName,
  medicineId,
  medicineName,
  currentRate,
  orderId,
}) => {
  const patientKey = toId(patientId);
  const medicineKey = toId(medicineId);
  const orderKey = toId(orderId);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  /*
   * Which medicine the rows on screen belong to.
   *
   * The panel is re-read whenever its subject changes - and also whenever the
   * order's own id does, which happens the moment Submit writes an order that
   * did not exist before. Tearing the table down for a spinner on that second
   * kind of read collapsed the panel to a line and opened it again a moment
   * later, which is the flicker Submit showed. A read about the same medicine
   * leaves what is on screen where it is until the new answer arrives; only a
   * read about a different one has nothing honest to show in the meantime.
   */
  const [shownFor, setShownFor] = useState("");
  const subject = `${patientKey ?? ""}|${medicineKey ?? ""}`;

  /* Opened on one medicine, read for another: the ledger folds again rather
     than carrying the last line's "View all 9 sales" over to a new one. */
  useEffect(() => {
    setOpen(false);
  }, [patientKey, medicineKey]);

  useEffect(() => {
    if (!patientKey || !medicineKey) {
      setRows([]);
      setFailed(false);
      return undefined;
    }
    let live = true;
    setLoading(true);
    setFailed(false);

    const params = new URLSearchParams({
      patient: String(patientKey),
      pharmacy_medicine: String(medicineKey),
    });
    if (orderKey) params.set("order_id", String(orderKey));

    getPharmacyMedicineHistoryApi(params)
      .then((res) => {
        if (!live) return;
        const data = res?.data;
        setRows(Array.isArray(data) ? data : data?.results || []);
        setShownFor(subject);
      })
      .catch(() => {
        if (!live) return;
        setRows([]);
        setFailed(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
    };
    /* `subject` is these two spelled as one string; it moves only when they
       do, so it buys no extra reads. */
  }, [patientKey, medicineKey, orderKey, subject]);

  if (!medicineKey) return null;

  if (!patientKey) {
    return (
      <HistoryNote>
        Choose the patient above to see what they have paid for{" "}
        {medicineName || "this medicine"} before.
      </HistoryNote>
    );
  }

  /* Showing another medicine's ledger, or nothing at all, while this one is
     being fetched would be a lie - that is what the spinner is for. */
  const stale = shownFor !== subject;
  if (loading && (stale || !rows.length)) return <HistoryLoading />;

  if (failed) {
    return <HistoryNote>Could not load the price history for this medicine.</HistoryNote>;
  }

  if (!rows.length) {
    return (
      <HistoryNote>
        {/*
          * The ledger is built from bills, not from orders - a medicine that
          * was put on an order which was never billed has no price to report.
          * Saying so beats "has not been sold before", which reads as a flat
          * denial that it was ever supplied.
          */}
        No billed sale of {medicineName || "this medicine"} to{" "}
        {patientName || "this patient"} yet. Earlier orders appear here once
        they have been billed.
      </HistoryNote>
    );
  }

  /* Newest first, whatever order the server sends them in. */
  const ordered = [...rows]
    .map(readRow)
    .sort((a, b) => (b.date?.valueOf() ?? 0) - (a.date?.valueOf() ?? 0));

  const delta = rateDelta(currentRate, ordered[0].net, {
    higher: (by) => `↑ Today ${formatRupees(by)} higher`,
    lower: (by) => `↓ Today ${formatRupees(by)} lower`,
    same: "Same rate today",
  });

  /* Folded, the panel is the last sale alone - every column still there. */
  const shown = open ? ordered : ordered.slice(0, 1);

  return (
    <HistoryStrip
      subject={[medicineName, patientName].filter(Boolean).join(" · ")}
      delta={delta}
      toggle={
        ordered.length > 1
          ? {
              open,
              onToggle: () => setOpen((was) => !was),
              label: open ? "Show last sale only" : `View all ${ordered.length} sales`,
            }
          : null
      }
      columns={COLUMNS}
      rows={shown.map((entry, index) => {
        const disc = discountPercent(entry);
        return {
          key: `${entry.billId ?? "row"}-${entry.batch ?? ""}-${index}`,
          dim: ["mrp", "disc", "gst", "saleType"],
          strong: ["bill"],
          cells: {
            bill: entry.bill,
            date: entry.date ? entry.date.format("DD MMM YYYY") : null,
            strip: entry.strip,
            /* Where the server splits strips from tablets both columns are
               its own; where it reports one quantity it is the loose count,
               which is the column that can hold it. */
            tab: entry.tab ?? (entry.strip === null ? entry.quantity : null),
            batch: entry.batch,
            mrp: money(entry.mrp),
            rate: money(entry.rate),
            disc: disc ? `${disc}%` : null,
            net: <b>{money(entry.net)}</b>,
            gst: entry.gst === null ? null : `${entry.gst}%`,
            expiry: entry.expiry ? dayjs(entry.expiry).format("DD MMM YYYY") : null,
            saleType: entry.saleType,
          },
        };
      })}
    />
  );
};

export default InlinePriceHistory;
